import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InAppBrowserObject } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import {
  catchError,
  filter,
  firstValueFrom,
  from,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { OAuthResponse, RefreshTokenResponse } from 'src/app/shared/models/fusion-auth.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { StorageService } from 'src/app/shared/services/storage.service';
import { UserPermissionValidationService } from 'src/app/shared/services/user-permission-validation.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { createRandomString, sha256hash, urlSafeEncode } from 'src/app/shared/utilities/security';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FusionAuthService {
  private readonly REFRESH_TOKEN_ENDPOINT = '/api/jwt/refresh';

  private readonly FUSION_AUTH_CODE_VERIFIER_KEY = 'fusionauth_code_verifier';

  private codeVerifier: string;

  private codeChallenge: string;

  private browser: InAppBrowserObject | null = null;

  private loadStartSubscription: Subscription | null = null;

  private exitSubscription: Subscription | null = null;

  private loader: HTMLIonLoadingElement;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private authTokenService: AuthTokenService,
    private analyticsService: AnalyticsService,
    private platform: Platform,
    private storage: StorageService,
    private router: Router,
    private utilityService: UtilityService,
    private userPermissionValidationService: UserPermissionValidationService,
    private accountService: AccountService,
  ) {}

  /**
   * Getter to decide if FA will be used or not. If you want to enable FA for the local development then return true.
   */
  get isFusionAuthConnectionEnabled(): boolean {
    return this.isRunningInMobile;
  }

  /**
   * Method to handel the OAuth login.
   */
  async oAuthLogin(): Promise<void> {
    this.loader = await this.utilityService.showLoading('Loading login page...');
    return this.generatePKCE().then(() => {
      const oAuthUrl = this.buildUrlWithParams(`${environment.fusionAuthConfig.url}/oauth2/authorize`, {
        client_id: environment.fusionAuthConfig.clientId,
        redirect_uri: this.oAuthCallbackUrl,
        response_type: 'code',
        code_challenge: this.codeChallenge,
        code_challenge_method: 'S256',
        scope: 'offline_access',
      });
      this.browser = this.utilityService.openExternalLink(oAuthUrl, this.urlTarget, this.inAppBrowserOptions);

      this.loadStartSubscription = this.browser.on('loadstart').subscribe((event) => {
        if (event.url.includes(this.oAuthCallbackUrl)) {
          const url = new URL(event.url);
          const code = url.searchParams.get('code');
          this.router.navigate(['/oauth-callback'], { queryParams: { code } });
          this.browser.close();
          this.loader.dismiss();
        }
      });
      this.onBrowserExit();
    });
  }

  /**
   * Method to handle the OAuth logout.
   */
  oAuthLogout(): void {
    const oAuthLogoutUrl = this.buildUrlWithParams(`${environment.fusionAuthConfig.url}/oauth2/logout`, {
      client_id: environment.fusionAuthConfig.clientId,
      tenantId: environment.fusionAuthConfig.tenantId,
      post_logout_redirect_uri: this.logoutCallbackUrl,
    });
    this.browser = this.utilityService.openExternalLink(oAuthLogoutUrl, this.urlTarget, 'location=no');

    this.loadStartSubscription = this.browser.on('loadstart').subscribe((event) => {
      if (event.url.includes(this.logoutCallbackUrl)) {
        this.router.navigate(['/logout-callback']);
        this.browser.close();
      }
    });

    this.onBrowserExit();
  }

  /**
   * Method to handle the oauth-callback.
   *
   * @param code - code returned by fusion auth in the params.
   * @returns Observable<null>
   */
  handleOAuthCallback(code: string): Observable<void> {
    return from(this.storage.get(this.FUSION_AUTH_CODE_VERIFIER_KEY)).pipe(
      switchMap((codeVerifier) => {
        if (!codeVerifier) {
          console.error('Code verifier is missing');
          this.router.navigate(['/']);
          return of(undefined);
        }

        const oAuthTokenUrl = this.buildUrlWithParams(`${environment.fusionAuthConfig.url}/oauth2/token`, {
          grant_type: 'authorization_code',
          client_id: environment.fusionAuthConfig.clientId,
          redirect_uri: this.oAuthCallbackUrl,
          code_verifier: codeVerifier,
          code,
        });

        return this.http.post<OAuthResponse>(oAuthTokenUrl, {}).pipe(
          switchMap((response) => {
            this.authTokenService.setAccessToken(response.access_token);
            this.authTokenService.setRefreshToken(response.refresh_token);
            this.authService.analyticsTrackLogin();
            this.resetCodeVerifier();

            return from(this.accountService.updateCurrentAndTargetUser()).pipe(
              switchMap(() => from(this.userPermissionValidationService.handlePostLoginValidations())),
              filter((isValid) => isValid),
              switchMap(() => this.authService.markUserAsLoggedIn()),
              tap(() => this.router.navigate(['/'])),
            );
          }),
          catchError((error: unknown) => {
            console.error('Error while exchanging the tokens', error);
            this.resetCodeVerifier();
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  /**
   * API call to refresh the access token.
   */
  refreshAccessToken(): Observable<void> {
    return from(this.authTokenService.refreshToken()).pipe(
      switchMap((refreshToken) =>
        this.http.post<RefreshTokenResponse>(
          `${environment.fusionAuthConfig.url}${this.REFRESH_TOKEN_ENDPOINT}`,
          { refreshToken },
          {
            headers: new HttpHeaders({ 'X-FusionAuth-TenantId': environment.fusionAuthConfig.tenantId }),
          },
        ),
      ),
      switchMap((response) => {
        const accessToken = response.token;
        if (!accessToken) {
          return throwError(
            () => new Error(`Error code: ${response.status}. New access token not issued, please login again.`),
          );
        }
        this.authTokenService.setAccessToken(accessToken);
        this.authService.markUserAsLoggedIn();
        this.analyticsService.track('Access Token Refreshed', {}, AnalyticsServicesNameEnum.SEGMENT_SERVICE);

        return of(undefined);
      }),
      catchError((error: unknown) =>
        // Modify it to logout the user as well after refreshTokenWithFallback is removed.
        // from(this.authService.logout()).pipe(switchMap(() => throwError(() => error))),
        throwError(() => error),
      ),
    );
  }

  /**
   * Method to decide the token refresh path based on feature flag.
   */
  handleTokenRefresh(): Promise<void> {
    if (this.isFusionAuthConnectionEnabled) {
      return firstValueFrom(this.refreshTokenWithFallback());
    }
    return this.authService.refreshAccessToken();
  }

  /**
   * Method to handle the fallback case for the non migtrated users.
   */
  private refreshTokenWithFallback(): Observable<void> {
    return this.refreshAccessToken().pipe(
      catchError((error: unknown) => {
        console.error('Error while refreshing token from FusionAuth. Retrying the platform API.', error);
        return this.authService.refreshAccessToken();
      }),
    );
  }

  /**
   * Method to generate PKCE code and challenge for OAuth login.
   */
  private async generatePKCE(): Promise<void> {
    this.codeVerifier = createRandomString(128);
    const codeChallenge = await sha256hash(this.codeVerifier);
    this.codeChallenge = urlSafeEncode(codeChallenge);
    await this.storage.ready();
    await this.storage.set(this.FUSION_AUTH_CODE_VERIFIER_KEY, this.codeVerifier);
  }

  /**
   * Method to create URL and attach search params.
   *
   * @param endpoint - the base URL.
   * @param params - key, value object of the params.
   * @returns Complete URL with the params.
   */
  private buildUrlWithParams(endpoint: string, params: Record<string, string>): string {
    const url = new URL(endpoint);
    const searchParams = new URLSearchParams(params);
    url.search = searchParams.toString();
    return url.toString();
  }

  /**
   * Method to delete the saved code verifier, post login.
   */
  private async resetCodeVerifier(): Promise<void> {
    await this.storage.remove(this.FUSION_AUTH_CODE_VERIFIER_KEY);
  }

  /**
   * Method to return target to open fusionAuth urls.
   */
  private get urlTarget(): string {
    // avoid opening a new browser tab when not running in the mobile app
    return this.isRunningInMobile ? '_blank' : '_self';
  }

  /**
   * Method to check if app is running on mobile.
   */
  private get isRunningInMobile(): boolean {
    return this.platform.is('cordova');
  }

  /**
   * Getter to decide the oAuthCallbackUrl based on the isLocalPointingToOtherEnv value.
   */
  private get oAuthCallbackUrl(): string {
    return this.isLocalPointingToOtherEnv
      ? `${window.location.origin}/oauth-callback`
      : environment.fusionAuthConfig.oAuthCallbackUrl;
  }

  /**
   * Getter to decide the logoutCallbackUrl based on the isLocalPointingToOtherEnv value.
   */
  private get logoutCallbackUrl(): string {
    return this.isLocalPointingToOtherEnv
      ? `${window.location.origin}/logout-callback`
      : environment.fusionAuthConfig.logoutCallbackUrl;
  }

  /**
   * Getter to check if the client BE is pointing to Release or other non local env.
   */
  private get isLocalPointingToOtherEnv(): boolean {
    return !this.isRunningInMobile && environment.environmentName !== 'local';
  }

  /**
   * In app browser options to open the URL within app
   */
  private get inAppBrowserOptions(): string {
    const options = {
      location: 'yes',
      toolbarcolor: '#e8e6e6',
      toolbarposition: 'top',
      closebuttoncaption: 'Close',
      closebuttoncolor: '#007aff',
      navigationbuttoncolor: '#000000',
      clearcache: 'yes',
      cleardata: 'yes',
      clearsessioncache: 'yes',
      zoom: 'no',
    };
    return Object.entries(options)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
  }

  /**
   * On closing the browser unsubscribe from all events
   */
  private onBrowserExit(): void {
    this.loader.dismiss();
    this.exitSubscription = this.browser.on('exit').subscribe((event) => {
      this.unsubscribeFromEvents();
    });
  }

  /**
   * Method to unsubscribe browser events
   */
  private unsubscribeFromEvents(): void {
    if (this.loadStartSubscription) {
      this.loadStartSubscription.unsubscribe();
      this.loadStartSubscription = null;
    }
    if (this.exitSubscription) {
      this.exitSubscription.unsubscribe();
      this.exitSubscription = null;
    }
    this.browser = null;
  }
}
